/**
 * Channel-settings BOOLEAN handling.
 *
 * HTML checkboxes default `value` to `"on"`. Formik 2.x then stores `['on']`
 * when the current field value is not already a boolean (typical on first save).
 * Django BooleanField rejects that payload.
 *
 * Use these helpers for setting toggles only — not mapping datasets or text fields.
 */

const TRUTHY_SETTING_STRINGS = new Set(['true', '1', 'yes', 'y', 'on'])
const FALSY_SETTING_STRINGS = new Set(['false', '0', 'no', 'n', 'off', ''])
const IS_FLAG_KEY = /^is[A-Z_]/

export type SettingBooleanFieldSetter = (field: string, value: boolean) => void

const isPlainObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const normalizeSettingString = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase()
  if (TRUTHY_SETTING_STRINGS.has(normalized)) return true
  if (FALSY_SETTING_STRINGS.has(normalized)) return false
  return null
}

/**
 * True for Formik checkbox-group leftovers such as `['on']`.
 * Empty arrays and object rows (field mappings) are not treated as booleans.
 */
export const isCheckboxGroupBooleanValue = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) return false

  return value.every((item) => {
    if (typeof item === 'boolean') return true
    if (typeof item === 'number') return item === 0 || item === 1
    if (typeof item !== 'string') return false
    return normalizeSettingString(item) !== null
  })
}

/** Coerce a setting-toggle value to a real boolean for save / checked state. */
export const normalizeBooleanSettingValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') return normalizeSettingString(value) === true
  if (Array.isArray(value)) {
    if (value.length === 0) return false
    return value.some((item) => normalizeBooleanSettingValue(item))
  }
  return false
}

/** UI gate for a setting toggle (parent ON, child panel visible, etc.). */
export const isTruthySettingValue = (value: unknown): boolean =>
  normalizeBooleanSettingValue(value)

export const resolveSettingFieldPath = (
  settingField: string | undefined | null,
  fieldName: string
): string => (settingField ? `${settingField}_setting.${fieldName}` : fieldName)

export const handleBooleanSettingChange =
  (setFieldValue: SettingBooleanFieldSetter, fieldPath: string) =>
  (event: {target: {checked: boolean}}) => {
    setFieldValue(fieldPath, Boolean(event.target.checked))
  }

const ensureParentClone = (
  values: Record<string, any>,
  parentKey: string,
  clonedParents: Set<string>
) => {
  if (
    !clonedParents.has(parentKey) &&
    isPlainObject(values[parentKey])
  ) {
    values[parentKey] = {...values[parentKey]}
    clonedParents.add(parentKey)
  }
}

const normalizeNullIsFlags = (obj: Record<string, any>) => {
  if (!obj || typeof obj !== 'object') return

  Object.keys(obj).forEach((key) => {
    const value = obj[key]

    if (isPlainObject(value)) {
      obj[key] = {...value}
      normalizeNullIsFlags(obj[key])
    } else if ((value === null || value === undefined) && IS_FLAG_KEY.test(key)) {
      obj[key] = false
    }
  })
}

/**
 * Sanitize form values before POST `/api/create-platform-settings`.
 *
 * - Known BOOLEAN catalog paths are always coerced.
 * - `['on']` leftovers on any `*_setting` container are coerced.
 * - Mapping arrays (`[]` / object rows) are left unchanged.
 * - Null / undefined `is*` flags become `false` (existing behavior).
 */
export const sanitizeBooleanSettingFields = <T extends Record<string, any>>(
  valuesObj: T,
  booleanFieldPaths: Iterable<string> = []
): T => {
  if (!valuesObj || typeof valuesObj !== 'object') {
    return valuesObj
  }

  const sanitizedValues: Record<string, any> = {...valuesObj}
  const clonedParents = new Set<string>()

  for (const path of booleanFieldPaths) {
    const [parentKey, fieldKey] = `${path}`.split('.')
    if (!parentKey || !fieldKey) continue
    if (!isPlainObject(sanitizedValues[parentKey])) continue

    ensureParentClone(sanitizedValues, parentKey, clonedParents)
    sanitizedValues[parentKey][fieldKey] = normalizeBooleanSettingValue(
      sanitizedValues[parentKey][fieldKey]
    )
  }

  Object.keys(sanitizedValues).forEach((parentKey) => {
    if (!parentKey.endsWith('_setting') || !isPlainObject(sanitizedValues[parentKey])) {
      return
    }

    ensureParentClone(sanitizedValues, parentKey, clonedParents)
    const container = sanitizedValues[parentKey]

    Object.keys(container).forEach((fieldKey) => {
      const value = container[fieldKey]
      if (isCheckboxGroupBooleanValue(value)) {
        container[fieldKey] = normalizeBooleanSettingValue(value)
      }
    })
  })

  normalizeNullIsFlags(sanitizedValues)

  return sanitizedValues as T
}
